// Crosshair editor page: shape palette, 1–5 layers, every parameter, hit FX,
// live preview, saved slots and share codes.
//
// The form is generated from the shape/profile schemas, so adding a parameter
// or shape never means touching this file's layout.

import { CrosshairAnimator } from "../crosshair/animate";
import { cloneStore, defaultConfig, getSlot, makeLayer } from "../crosshair/config";
import { decodeCrosshair, encodeCrosshair } from "../crosshair/codec";
import { configBounds, drawScene, renderThumbnail } from "../crosshair/render";
import { SHAPE_DEFS, SHAPE_ICONS, SHAPE_IDS, normalizeParams } from "../crosshair/shapes";
import {
  MAX_FX,
  MAX_LAYERS,
  newId,
  type CrosshairConfig,
  type CrosshairStore,
  type Easing,
  type Fx,
  type Layer,
  type ShapeId,
  type Trigger,
  EASINGS,
  RETRIGGERS,
  TRIGGERS,
} from "../crosshair/types";
import { dsElement } from "./daisy";

export interface CrosshairEditorCallbacks {
  onChange: (store: CrosshairStore) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function num(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onInput: (v: number) => void,
  unit?: string,
): HTMLElement {
  const wrap = el("label", "xhair-field");
  const cap = el("span", "", unit ? `${label} (${unit})` : label);
  const input = el("input");
  input.type = "number";
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  dsElement(input, "input", { size: "sm", keep: "w-full" });
  input.addEventListener("input", () => {
    const v = Number(input.value);
    if (!Number.isFinite(v)) return;
    onInput(Math.min(max, Math.max(min, v)));
  });
  wrap.append(cap, input);
  return wrap;
}

/** Labelled numeric field with a range slider + number box. */
function sliderRow(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onInput: (v: number) => void,
  unit = "",
): HTMLElement {
  const row = el("div", "profile-row");
  row.append(el("label", "", label));
  const combo = el("div", "combo");
  const range = el("input");
  range.type = "range";
  range.min = String(min);
  range.max = String(max);
  range.step = String(step);
  range.value = String(value);
  dsElement(range, "range", { keep: "w-full" });
  const box = el("input");
  box.type = "number";
  box.min = String(min);
  box.max = String(max);
  box.step = String(step);
  box.value = String(value);
  dsElement(box, "input", { size: "sm", keep: "w-20" });
  const apply = (v: number) => {
    const c = Math.min(max, Math.max(min, v));
    if (Number(range.value) !== c) range.value = String(c);
    if (Number(box.value) !== c) box.value = String(c);
    onInput(c);
  };
  range.addEventListener("input", () => apply(Number(range.value)));
  box.addEventListener("input", () => {
    const v = Number(box.value);
    if (Number.isFinite(v)) apply(v);
  });
  combo.append(range, box);
  row.append(combo, el("span", "inherit", unit));
  return row;
}

function checkRow(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = el("div", "profile-row");
  row.append(el("label", "", label));
  const combo = el("div", "combo");
  const box = el("input");
  box.type = "checkbox";
  box.checked = value;
  dsElement(box, "toggle", { color: "primary", size: "sm" });
  box.addEventListener("change", () => onChange(box.checked));
  combo.append(box);
  row.append(combo, el("span", "", ""));
  return row;
}

function selectRow(
  label: string,
  value: string,
  options: { value: string; label: string }[],
  onChange: (v: string) => void,
): HTMLElement {
  const row = el("div", "profile-row");
  row.append(el("label", "", label));
  const combo = el("div", "combo");
  const sel = el("select");
  for (const o of options) {
    const opt = el("option", "", o.label);
    opt.value = o.value;
    sel.append(opt);
  }
  sel.value = value;
  sel.addEventListener("change", () => onChange(sel.value));
  combo.append(sel);
  row.append(combo, el("span", "", ""));
  return row;
}

export class CrosshairEditor {
  private store: CrosshairStore;
  private root: HTMLElement;
  private layersEl!: HTMLElement;
  private slotsEl!: HTMLElement;
  private inUseEl!: HTMLElement;
  private previewCanvas!: HTMLCanvasElement;
  private codeEl!: HTMLTextAreaElement;
  private nameEl!: HTMLInputElement;
  private animator: CrosshairAnimator;
  private active = false;
  private raf = 0;
  private lastDpr = 0;
  private loopPreview = false;
  private lastLoopFire = 0;

  constructor(
    host: HTMLElement,
    store: CrosshairStore,
    private cb: CrosshairEditorCallbacks,
  ) {
    this.store = cloneStore(store);
    this.root = host;
    this.animator = new CrosshairAnimator(this.config());
    this.buildShell();
    this.build();
  }

  private config(): CrosshairConfig {
    return getSlot(this.store, this.store.activeId).config;
  }

  setStore(store: CrosshairStore): void {
    this.store = cloneStore(store);
    this.animator.setConfig(this.config());
    this.build();
  }

  setActiveSlot(id: string): void {
    this.store.activeId = id;
    this.animator.setConfig(this.config());
    this.build();
    this.push();
  }

  activate(): void {
    this.active = true;
    this.animator.setConfig(this.config());
    if (!this.raf) this.raf = requestAnimationFrame(this.loop);
  }

  deactivate(): void {
    this.active = false;
  }

  private loop = (): void => {
    this.raf = this.active ? requestAnimationFrame(this.loop) : 0;
    if (!this.active) return;
    const now = performance.now();
    if (this.loopPreview && now - this.lastLoopFire > 900) {
      this.lastLoopFire = now;
      this.animator.fire("hit", now);
    }
    this.renderPreview(now);
  };

  private push(): void {
    this.animator.setConfig(this.config());
    this.cb.onChange(cloneStore(this.store));
  }

  /** Structural change: rebuild controls + preview. */
  private commit(): void {
    this.animator.setConfig(this.config());
    this.cb.onChange(cloneStore(this.store));
    this.buildLayers();
    this.buildSlots();
  }

  /**
   * Value change: no DOM rebuild (keeps focus), just re-render + save. When an
   * effect field changes, fire that effect so the edit is visible immediately.
   */
  private touch(fx?: Fx): void {
    this.animator.setConfig(this.config());
    this.cb.onChange(cloneStore(this.store));
    if (fx) this.animator.fire(fx.trigger, performance.now());
    this.renderPreview(performance.now());
  }

  private buildShell(): void {
    this.root.innerHTML = "";
    const wrap = el("div", "xhair-editor");
    const main = el("div");
    const side = el("div", "xhair-side");

    main.append(el("div", "page-title", "Shapes"));
    const palette = el("div", "xhair-palette");
    for (const id of SHAPE_IDS) {
      const b = el("button", "xhair-shape");
      b.title = `Add / switch to ${SHAPE_DEFS[id].label}`;
      b.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="${SHAPE_ICONS[id]}"/></svg>`;
      b.addEventListener("click", () => this.applyShape(id));
      palette.append(b);
    }
    main.append(palette, el("div", "page-title", "Layers"));
    this.layersEl = el("div");
    main.append(this.layersEl);

    const box = el("div", "xhair-preview-box");
    this.previewCanvas = el("canvas");
    box.append(this.previewCanvas);
    side.append(el("div", "page-title", "Preview"), box);

    const hits = el("div", "xhair-actions");
    for (const t of ["hit", "kill", "damaged", "fire"] as Trigger[]) {
      const b = el("button", "xhair-btn", t);
      dsElement(b, "button", { style: "outline", size: "xs" });
      b.addEventListener("click", () => {
        this.animator.fire(t, performance.now(), t === "hit" ? 1 : 1);
        this.renderPreview(performance.now());
      });
      hits.append(b);
    }
    const loopLabel = el("label", "xhair-loop");
    const loopBox = el("input");
    loopBox.type = "checkbox";
    loopBox.checked = this.loopPreview;
    dsElement(loopBox, "toggle", { color: "primary", size: "sm" });
    loopBox.title = "repeat the hit preview";
    loopBox.addEventListener("change", () => {
      this.loopPreview = loopBox.checked;
      this.lastLoopFire = 0;
    });
    loopLabel.append(loopBox, el("span", "", "loop hit"));
    hits.append(loopLabel);
    side.append(hits);

    side.append(el("div", "page-title", "Saved"));
    this.slotsEl = el("div", "xhair-slots");
    this.inUseEl = el("div", "xhair-inuse");
    side.append(this.slotsEl, this.inUseEl);

    this.nameEl = el("input");
    this.nameEl.type = "text";
    this.nameEl.maxLength = 24;
    this.nameEl.placeholder = "slot name";
    dsElement(this.nameEl, "input", { size: "sm", keep: "w-full" });
    side.append(this.nameEl);
    const actions = el("div", "xhair-actions");
    const add = el("button", "xhair-btn", "New slot");
    dsElement(add, "button", { style: "outline", size: "sm" });
    add.addEventListener("click", () => this.newSlot());
    const rename = el("button", "xhair-btn", "Rename");
    dsElement(rename, "button", { style: "outline", size: "sm" });
    rename.addEventListener("click", () => this.renameSlot());
    const del = el("button", "xhair-btn", "Delete");
    dsElement(del, "button", { style: "outline", size: "sm" });
    del.addEventListener("click", () => this.deleteSlot());
    const reset = el("button", "xhair-btn", "Reset");
    dsElement(reset, "button", { style: "outline", size: "sm" });
    reset.title = "reset this crosshair slot to the shipped design";
    reset.addEventListener("click", () => {
      const slot = getSlot(this.store, this.store.activeId);
      slot.config = defaultConfig();
      this.commit();
    });
    actions.append(add, rename, del, reset);
    side.append(actions);

    this.codeEl = el("textarea", "xhair-code");
    this.codeEl.placeholder = "wbx1.… share code";
    dsElement(this.codeEl, "textarea", { size: "sm", keep: "xhair-code w-full" });
    side.append(this.codeEl);
    const codeActions = el("div", "xhair-actions");
    const copy = el("button", "xhair-btn", "Copy code");
    dsElement(copy, "button", { style: "outline", size: "sm" });
    copy.addEventListener("click", () => this.copyCode());
    const paste = el("button", "xhair-btn", "Paste code");
    dsElement(paste, "button", { style: "outline", size: "sm" });
    paste.addEventListener("click", () => this.pasteCode());
    codeActions.append(copy, paste);
    side.append(codeActions);

    wrap.append(main, side);
    this.root.append(wrap);
  }

  private build(): void {
    this.buildLayers();
    this.buildSlots();
    this.nameEl.value = getSlot(this.store, this.store.activeId).name;
    this.renderPreview(performance.now());
  }

  private applyShape(id: ShapeId): void {
    const layer = this.selectedLayer();
    layer.shape = id;
    layer.params = normalizeParams(id, layer.params);
    this.commit();
  }

  private selectedLayer(): Layer {
    const cfg = this.config();
    if (!cfg.layers.length) cfg.layers.push(makeLayer());
    return cfg.layers[Math.min(this.selected, cfg.layers.length - 1)];
  }

  private selected = 0;

  private buildLayers(): void {
    this.layersEl.innerHTML = "";
    const cfg = this.config();
    if (this.selected >= cfg.layers.length) this.selected = Math.max(0, cfg.layers.length - 1);
    cfg.layers.forEach((layer, i) => {
      const card = el("div", `xhair-layer${i === this.selected ? " open" : ""}`);
      const head = el("div", "xhair-layer-head");
      const vis = el("input");
      vis.type = "checkbox";
      vis.checked = layer.visible;
      dsElement(vis, "toggle", { color: "primary", size: "sm" });
      vis.title = "visible";
      vis.addEventListener("change", () => {
        layer.visible = vis.checked;
        this.touch();
      });
      const open = el("button", "xhair-btn", i === this.selected ? "▾" : "▸");
      dsElement(open, "button", { style: "ghost", size: "xs", shape: "square" });
      open.title = "expand";
      open.addEventListener("click", () => {
        this.selected = i;
        this.animator.setConfig(this.config());
        this.buildLayers();
      });
      head.append(vis, open, el("span", "name", `Layer ${i + 1}`));
      head.append(el("span", "shape-name", SHAPE_DEFS[layer.shape].label));
      const up = el("button", "xhair-btn", "↑");
      dsElement(up, "button", { style: "ghost", size: "xs", shape: "square" });
      up.disabled = i === 0;
      up.addEventListener("click", () => this.moveLayer(i, -1));
      const down = el("button", "xhair-btn", "↓");
      dsElement(down, "button", { style: "ghost", size: "xs", shape: "square" });
      down.disabled = i === cfg.layers.length - 1;
      down.addEventListener("click", () => this.moveLayer(i, 1));
      const dup = el("button", "xhair-btn", "⧉");
      dsElement(dup, "button", { style: "ghost", size: "xs", shape: "square" });
      dup.title = "duplicate";
      dup.addEventListener("click", () => this.duplicateLayer(i));
      const del = el("button", "xhair-btn", "✕");
      dsElement(del, "button", { style: "ghost", size: "xs", shape: "square" });
      del.title = "delete";
      del.disabled = cfg.layers.length <= 1;
      del.addEventListener("click", () => this.deleteLayer(i));
      head.append(up, down, dup, del);

      const body = el("div", "xhair-layer-body");
      if (i === this.selected) this.buildLayerBody(body, layer);
      card.append(head, body);
      this.layersEl.append(card);
    });

    if (cfg.layers.length < MAX_LAYERS) {
      const add = el("button", "xhair-btn", `+ Add layer (${cfg.layers.length}/${MAX_LAYERS})`);
      dsElement(add, "button", { style: "outline", size: "sm" });
      add.addEventListener("click", () => {
        cfg.layers.push(makeLayer("cross"));
        this.selected = cfg.layers.length - 1;
        this.commit();
      });
      this.layersEl.append(add);
    }
  }

  private buildLayerBody(body: HTMLElement, layer: Layer): void {
    const def = SHAPE_DEFS[layer.shape];

    // Shape first: it decides which parameters exist at all.
    body.append(
      selectRow(
        "Shape",
        layer.shape,
        SHAPE_IDS.map((id) => ({ value: id, label: SHAPE_DEFS[id].label })),
        (v) => {
          layer.shape = v as ShapeId;
          layer.params = normalizeParams(layer.shape, layer.params);
          this.commit();
        },
      ),
    );

    // Then the fields in the order a crosshair is actually tuned:
    // size -> arms/gap (shape details) -> colour -> placement -> hit effects.
    const SIZE = /(length|size|radius|width|height|thickness|inner|outer|scale)/i;
    const BORDER = /(gap|border|outline|corner|round|arm|top|right|bottom|left|rotate)/i;
    const buckets: Record<"size" | "border" | "detail", typeof def.params> = {
      size: [],
      border: [],
      detail: [],
    };
    for (const p of def.params) {
      const key = String(p.key);
      const bucket = p.kind === "bool" || BORDER.test(key)
        ? "border"
        : SIZE.test(key)
          ? "size"
          : "detail";
      buckets[bucket].push(p);
    }

    const renderParams = (heading: string, list: typeof def.params) => {
      if (list.length === 0) return;
      body.append(el("div", "xhair-section", heading));
      const g = el("div", "xhair-fx-grid");
      for (const p of list) {
        const cur = layer.params[p.key];
        if (p.kind === "bool") {
          g.append(
            checkRow(p.label, cur === true, (v) => {
              layer.params[p.key] = v;
              this.touch();
            }),
          );
        } else if (p.kind === "num") {
          g.append(
            num(
              p.label,
              typeof cur === "number" ? cur : 0,
              p.min ?? 0,
              p.max ?? 100,
              p.step ?? 1,
              (v) => {
                layer.params[p.key] = v;
                this.touch();
              },
              p.unit,
            ),
          );
        } else if (p.kind === "enum") {
          g.append(
            selectRow(
              p.label,
              String(cur ?? ""),
              p.options ?? [],
              (v) => {
                layer.params[p.key] = v;
                this.touch();
              },
            ),
          );
        }
      }
      body.append(g);
    };
    renderParams("Size", buckets.size);
    renderParams("Arms, gap & border", buckets.border);
    renderParams("Detail", buckets.detail);
    body.append(el("div", "xhair-section", "Colour & border"));
    const colorRow = el("div", "profile-row");
    colorRow.append(el("label", "", "Colour"));
    const combo = el("div", "combo");
    const color = el("input");
    color.type = "color";
    color.value = layer.color;
    color.addEventListener("input", () => {
      layer.color = color.value;
      this.touch();
    });
    combo.append(color);
    colorRow.append(combo, el("span", "", ""));
    body.append(colorRow);

    const outline = el("div", "profile-row");
    outline.append(el("label", "", "Outline"));
    const oc = el("div", "combo");
    const ocInput = el("input");
    ocInput.type = "color";
    ocInput.value = layer.outline.color;
    ocInput.addEventListener("input", () => {
      layer.outline.color = ocInput.value;
      this.touch();
    });
    const ot = el("input");
    ot.type = "number";
    ot.min = "0";
    ot.max = "16";
    ot.step = "0.5";
    ot.value = String(layer.outline.thickness);
    dsElement(ot, "input", { size: "sm", keep: "w-16" });
    dsElement(ot, "input", { size: "sm", keep: "w-16" });
    ot.addEventListener("input", () => {
      layer.outline.thickness = Math.max(0, Number(ot.value) || 0);
      this.touch();
    });
    const om = el("select");
    for (const v of ["scale", "persistent"]) {
      const o = el("option", "", v);
      o.value = v;
      om.append(o);
    }
    om.value = layer.outline.mode;
    om.addEventListener("change", () => {
      layer.outline.mode = om.value as "scale" | "persistent";
      this.touch();
    });
    oc.append(ocInput, ot, om);
    outline.append(oc, el("span", "inherit", "px"));
    body.append(outline);

    body.append(el("div", "xhair-section", "Placement"));
    body.append(
      sliderRow("Rotation", layer.rotation, -180, 180, 1, (v) => {
        layer.rotation = v;
        this.touch();
      }, "°"),
      sliderRow("Scale", layer.scale, 0.1, 3, 0.05, (v) => {
        layer.scale = v;
        this.touch();
      }),
      sliderRow("Opacity", layer.opacity, 0, 1, 0.05, (v) => {
        layer.opacity = v;
        this.touch();
      }),
      sliderRow("Offset X", layer.offset.x, -64, 64, 1, (v) => {
        layer.offset.x = v;
        this.touch();
      }, "px"),
      sliderRow("Offset Y", layer.offset.y, -64, 64, 1, (v) => {
        layer.offset.y = v;
        this.touch();
      }, "px"),
      checkRow("Additive blend", layer.blend === "additive", (v) => {
        layer.blend = v ? "additive" : "normal";
        this.touch();
      }),
    );


    // ---- Hit effects ------------------------------------------------------
    const fxHead = el("div", "xhair-fx-head");
    fxHead.append(el("span", "", "Hit effects"));
    const preset = el("select");
    for (const [value, label] of [
      ["grow", "grow"],
      ["shrink", "shrink"],
      ["spin", "spin"],
      ["flash", "flash"],
      ["colour", "hit colour"],
    ] as [string, string][]) {
      const o = el("option", "", label);
      o.value = value;
      preset.append(o);
    }
    preset.title = "effect preset for the next hit effect";
    const addFx = el("button", "xhair-btn", "+ effect");
    dsElement(addFx, "button", { style: "outline", size: "xs" });
    addFx.disabled = layer.fx.length >= MAX_FX;
    addFx.addEventListener("click", () => {
      layer.fx.push(this.defaultFx(preset.value));
      this.commit();
      // Show the new pulse immediately.
      this.animator.fire("hit", performance.now());
    });
    fxHead.append(preset, addFx);
    body.append(fxHead);
    body.append(
      el(
        "div",
        "xhair-note",
        "Effects belong to this layer. Use hit / kill / damaged / fire below the preview (or tick loop hit).",
      ),
    );

    layer.fx.forEach((fx, fi) => {
      const box = el("div", "xhair-fx");
      const head = el("div", "xhair-fx-head");
      const trig = el("select");
      for (const t of TRIGGERS) {
        const o = el("option", "", t);
        o.value = t;
        trig.append(o);
      }
      trig.value = fx.trigger;
      trig.addEventListener("change", () => {
        fx.trigger = trig.value as Trigger;
        this.touch(fx);
      });
      const rm = el("button", "xhair-btn", "✕");
      dsElement(rm, "button", { style: "ghost", size: "xs", shape: "square" });
      rm.title = "remove effect";
      rm.addEventListener("click", () => {
        layer.fx.splice(fi, 1);
        this.commit();
      });
      head.append(trig, el("span", "", "in"), rm);
      box.append(head);

      const g = el("div", "xhair-fx-grid");
      const addNum = (label: string, v: number, min: number, max: number, step: number, set: (n: number) => void) =>
        g.append(num(label, v, min, max, step, set));
      addNum("In (ms)", fx.attack, 0, 2000, 5, (v) => {
        fx.attack = v;
        this.touch(fx);
      });
      addNum("Hold (ms)", fx.hold, 0, 2000, 5, (v) => {
        fx.hold = v;
        this.touch(fx);
      });
      addNum("Out (ms)", fx.release, 0, 2000, 5, (v) => {
        fx.release = v;
        this.touch(fx);
      });
      g.append(
        selectRow(
          "Easing",
          fx.easing,
          EASINGS.map((e) => ({ value: e, label: e })),
          (v) => {
            fx.easing = v as Easing;
            this.touch(fx);
          },
        ),
        selectRow(
          "Retrigger",
          fx.retrigger,
          RETRIGGERS.map((r) => ({ value: r, label: r })),
          (v) => {
            fx.retrigger = v as Fx["retrigger"];
            this.touch(fx);
          },
        ),
      );
      addNum("Scale +", fx.scale, -1, 3, 0.05, (v) => {
        fx.scale = v;
        this.touch(fx);
      });
      addNum("Rotate +", fx.rotate, -720, 720, 5, (v) => {
        fx.rotate = v;
        this.touch(fx);
      });
      addNum("Gap +", fx.gap, -64, 64, 1, (v) => {
        fx.gap = v;
        this.touch(fx);
      });
      addNum("Width +", fx.thickness, -16, 16, 0.5, (v) => {
        fx.thickness = v;
        this.touch(fx);
      });
      addNum("Move X", fx.offsetX, -128, 128, 1, (v) => {
        fx.offsetX = v;
        this.touch(fx);
      });
      addNum("Move Y", fx.offsetY, -128, 128, 1, (v) => {
        fx.offsetY = v;
        this.touch(fx);
      });
      addNum("Opacity +", fx.opacity, -1, 1, 0.05, (v) => {
        fx.opacity = v;
        this.touch(fx);
      });
      box.append(g);

      const colors = el("div", "xhair-fx-grid");
      colors.append(
        this.nullableColor("Hit colour", fx.color, (v) => {
          fx.color = v;
          this.touch(fx);
        }),
        this.nullableColor("Outline colour", fx.outlineColor, (v) => {
          fx.outlineColor = v;
          this.touch(fx);
        }),
      );
      box.append(colors);
      body.append(box);
    });
  }

  private defaultFx(preset = "grow"): Fx {
    const fx: Fx = {
      id: newId("f"),
      trigger: "hit",
      attack: 40,
      hold: 40,
      release: 160,
      easing: "out",
      retrigger: "stack",
      scale: 0.25,
      rotate: 0,
      gap: 0,
      thickness: 0,
      offsetX: 0,
      offsetY: 0,
      opacity: 0,
      color: null,
      outlineColor: null,
    };
    if (preset === "shrink") fx.scale = -0.25;
    if (preset === "spin") {
      fx.rotate = 90;
      fx.scale = 0;
    }
    if (preset === "flash") {
      fx.color = "#ffffff";
      fx.scale = 0;
      fx.attack = 0;
      fx.hold = 60;
      fx.release = 140;
    }
    if (preset === "colour") {
      fx.color = "#ff3040";
      fx.scale = 0.1;
    }
    return fx;
  }

  private nullableColor(label: string, value: string | null, onChange: (v: string | null) => void): HTMLElement {
    const wrap = el("label", "xhair-field");
    wrap.append(el("span", "", label));
    const use = el("input");
    use.type = "checkbox";
    use.checked = value !== null;
    use.title = "enable this colour override";
    dsElement(use, "checkbox", { color: "primary", size: "sm" });
    const color = el("input");
    color.type = "color";
    color.value = value ?? "#ff3040";
    color.disabled = value === null;
    use.addEventListener("change", () => {
      color.disabled = !use.checked;
      onChange(use.checked ? color.value : null);
    });
    color.addEventListener("input", () => {
      if (use.checked) onChange(color.value);
    });
    const row = el("span", "combo");
    row.append(use, color);
    wrap.append(row);
    return wrap;
  }

  private moveLayer(i: number, dir: number): void {
    const cfg = this.config();
    const j = i + dir;
    if (j < 0 || j >= cfg.layers.length) return;
    const [l] = cfg.layers.splice(i, 1);
    cfg.layers.splice(j, 0, l);
    this.selected = j;
    this.commit();
  }

  private duplicateLayer(i: number): void {
    const cfg = this.config();
    if (cfg.layers.length >= MAX_LAYERS) return;
    const copy = JSON.parse(JSON.stringify(cfg.layers[i])) as Layer;
    copy.id = newId("l");
    copy.offset = { ...copy.offset };
    copy.outline = { ...copy.outline };
    copy.params = { ...copy.params };
    copy.fx = copy.fx.map((f) => ({ ...f, id: newId("f") }));
    cfg.layers.splice(i + 1, 0, copy);
    this.selected = i + 1;
    this.commit();
  }

  private deleteLayer(i: number): void {
    const cfg = this.config();
    if (cfg.layers.length <= 1) return;
    cfg.layers.splice(i, 1);
    this.selected = Math.max(0, Math.min(this.selected, cfg.layers.length - 1));
    this.commit();
  }

  private buildSlots(): void {
    this.slotsEl.innerHTML = "";
    const dpr = window.devicePixelRatio || 1;
    for (const slot of this.store.slots) {
      const b = el("button", `xhair-slot${slot.id === this.store.activeId ? " active" : ""}`);
      const img = el("img");
      img.src = renderThumbnail(slot.config, 40, dpr);
      img.alt = slot.name;
      const name = el("span", "", slot.name);
      b.append(img, name);
      b.title = slot.name;
      b.addEventListener("click", () => {
        this.store.activeId = slot.id;
        this.selected = 0;
        this.animator.setConfig(this.config());
        this.build();
        this.push();
      });
      if (slot.id !== "default") {
        const x = el("span", "x", "✕");
        x.title = "delete slot";
        x.addEventListener("click", (e) => {
          e.stopPropagation();
          this.deleteSlotById(slot.id);
        });
        b.append(x);
      }
      this.slotsEl.append(b);
    }
    this.inUseEl.textContent = `In use: ${getSlot(this.store, this.store.activeId).name} · selecting a saved crosshair applies it in game (per-weapon assignments still win)`;
  }

  private newSlot(): void {
    const cfg = this.config();
    const id = newId("s");
    const name = (this.nameEl.value || "Crosshair").slice(0, 24);
    this.store.slots.push({ id, name, config: JSON.parse(JSON.stringify(cfg)) as CrosshairConfig });
    this.store.activeId = id;
    this.commit();
    this.nameEl.value = name;
  }

  private renameSlot(): void {
    const slot = getSlot(this.store, this.store.activeId);
    slot.name = (this.nameEl.value || slot.name).slice(0, 24);
    this.commit();
  }

  private deleteSlot(): void {
    this.deleteSlotById(this.store.activeId);
  }

  private deleteSlotById(id: string): void {
    if (id === "default") return;
    this.store.slots = this.store.slots.filter((s) => s.id !== id);
    if (this.store.defaultId === id) this.store.defaultId = "default";
    if (this.store.activeId === id) this.store.activeId = this.store.defaultId;
    this.build();
    this.push();
  }

  private copyCode(): void {
    const code = encodeCrosshair(this.config());
    this.codeEl.value = code;
    this.codeEl.select();
    void navigator.clipboard?.writeText(code).catch(() => undefined);
  }

  private pasteCode(): void {
    const cfg = decodeCrosshair(this.codeEl.value);
    if (!cfg) {
      this.codeEl.value = "Invalid code";
      return;
    }
    const id = newId("s");
    this.store.slots.push({ id, name: "Imported", config: cfg });
    this.store.activeId = id;
    this.build();
    this.push();
  }

  private renderPreview(now: number): void {
    const c = this.previewCanvas;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const size = 200;
    const dpr = window.devicePixelRatio || 1;
    if (this.lastDpr !== dpr || c.width !== Math.round(size * dpr)) {
      c.width = Math.round(size * dpr);
      c.height = Math.round(size * dpr);
      c.style.width = `${size}px`;
      c.style.height = `${size}px`;
      this.lastDpr = dpr;
    }
    const cfg = this.config();
    const half = Math.max(1, configBounds(cfg));
    const fit = Math.min(8, (size * 0.36) / half);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(size / 2, size / 2);
    ctx.scale(fit, fit);
    drawScene(ctx, cfg, this.animator.sample(now), dpr * fit);
  }
}
